// A code in flight has to survive navigations that wipe the in-memory store —
// the org-switch hard reload, the OAuth round-trip, the post-signup navigation.
// A cookie is the carrier for the same reason `zcp-pool-claim-base.util.ts`
// uses one.

export interface PendingPromoCode {
  code: string;
}

export const PENDING_PROMO_CODE_COOKIE = 'pendingPromoCode';

// 14 days. The binding case is not any of the in-app hops — the org switch and
// the OAuth round-trip are over in seconds — but the gap between scanning a
// physical coin and actually signing up, which can be that evening or the
// following week. The code is also the marketing source the backend records
// (`marketingSource: "promo-code|<code>"`, preferred over `reference`), so a
// window that expires takes the attribution with it, not just the credit. The
// `reference` cookie's 24h is the floor for that job; a coin outlives a
// referral link, so this sits well above it without running a month-long
// attribution window.
//
// A long life costs little: the cookie is drained the first time the promo
// section is reached, so it cannot keep redirecting anyone, and the code was
// theirs to redeem the whole time.
const PENDING_PROMO_CODE_COOKIE_MAX_AGE = 1209600;

const CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * The deep link carries the code base64url-encoded so it cannot be read off the
 * address bar of a slow-loading page.
 *
 * A raw code is itself valid base64url, so charset alone cannot tell the two
 * apart — but round-tripping can. A correctly encoded link ALWAYS decodes back
 * to something matching CODE_PATTERN (guaranteed, since that is what was
 * encoded), while a raw code decodes to bytes that almost never all fall in
 * that set. Measured over 200k generated codes per shape: 0% for anything
 * carrying a category prefix or >=16 chars, 0.03% worst case for short
 * unprefixed ones. When it does misfire the decoded value is visibly wrong in
 * the pre-filled field, so it fails loudly rather than silently.
 */
export const decodePromoCode = (segment: string): string => {
  if (!segment) { return ''; }

  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);

  try {
    const decoded = atob(padded);
    return CODE_PATTERN.test(decoded) ? decoded : segment;
  } catch {
    return segment;
  }
};

export const setPendingPromoCode = (code: string): void => {
  if (typeof document === 'undefined' || !code) { return; }

  document.cookie = `${PENDING_PROMO_CODE_COOKIE}=${encodeURIComponent(code)}`
    + `; path=/; max-age=${PENDING_PROMO_CODE_COOKIE_MAX_AGE}; SameSite=Lax`;
};

export const readPendingPromoCode = (): PendingPromoCode | undefined => {
  if (typeof document === 'undefined') { return undefined; }

  const row = document.cookie
    .split('; ')
    .find((r) => r.startsWith(`${PENDING_PROMO_CODE_COOKIE}=`));

  if (!row) { return undefined; }

  // A tampered or foreign value can make decodeURIComponent throw, and this is
  // read from inside login/OAuth dispatch chains where that would take the
  // whole sign-in down over a cookie nobody asked for.
  let code: string;
  try {
    code = decodeURIComponent(row.slice(`${PENDING_PROMO_CODE_COOKIE}=`.length));
  } catch {
    return undefined;
  }

  if (!code) { return undefined; }

  return { code };
};

export const clearPendingPromoCode = (): void => {
  if (typeof document === 'undefined') { return; }
  document.cookie = `${PENDING_PROMO_CODE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

/** Read-and-clear: the handover is consumed exactly once, by whoever gets there first. */
export const drainPendingPromoCode = (): PendingPromoCode | undefined => {
  const pending = readPendingPromoCode();
  clearPendingPromoCode();
  return pending;
};

export const hasPendingPromoCode = (): boolean => !!readPendingPromoCode();


// The failures that mean "the code specifically is the problem". A registration
// carrying one of these is rolled back whole, so the code has to be dropped
// before the user can get an account at all.
const PROMO_CODE_ERROR_CODES = new Set([
  'invalidPromoCode',
  'promoCodeNotYetValid',
  'promoCodeExpired',
  'promoCodeExhausted',
  'promoCodeAlreadyRedeemedInCategory',
  'promoCodeOnlyForNewClients',
  'newClientPromoCodeAlreadyUsed',
  'promoCodeRequiresPayment',
  'promoCodeRequiresVerifiedClient',
  'promoCreditLimitReached'
]);

export const isPromoCodeError = (code: string): boolean => PROMO_CODE_ERROR_CODES.has(code);

/**
 * Two different reasons, one way out. `promoCodeRequiresPayment` is a top-up
 * bonus by nature and never redeems on its own; `promoCodeRequiresVerifiedClient`
 * is a code the account is not yet eligible for, which clears permanently once
 * it has paid. Attaching either to a top-up works immediately — including the
 * customer's very first payment — so both earn the same link across, and
 * neither is an invalid code.
 */
const TOP_UP_RESOLVABLE_ERROR_CODES = new Set([
  'promoCodeRequiresPayment',
  'promoCodeRequiresVerifiedClient'
]);

export const isResolvedByTopUp = (code: string): boolean => TOP_UP_RESOLVABLE_ERROR_CODES.has(code);

/**
 * Whether the code taken at sign-up is still waiting to be applied.
 *
 * Both fields are needed and neither is enough alone.
 * `signUpPromoCodeRequiresPayment` describes the CODE — that it needs a payment
 * to land — and stays true afterwards, so on its own it would go on claiming
 * the code is pending long after it had ridden a top-up.
 * `verifiedByTopUp` is what says the account has actually paid, which is the
 * event that spends it.
 *
 * Typed structurally so this file stays free of RUNTIME imports: it is
 * leaf-imported by specs whose jest transform chokes on the graph the barrel
 * drags in. The one import above is a type and is erased.
 */
export const hasParkedSignUpPromoCode = (client: {
  signUpPromoCodeRequiresPayment?: boolean;
  verifiedByTopUp?: boolean;
} | undefined | null): boolean =>
  !!client?.signUpPromoCodeRequiresPayment && !client.verifiedByTopUp;

// Why a promo code was dropped, carried across an OAuth round-trip. The signup
// that rejected it cannot simply be replayed — the provider's authorization
// code is exchanged before the promo code is ever validated, so it is already
// spent by the time the error comes back and only a fresh round-trip can
// retry. That redirect leaves the app, so the reason has to travel as a cookie
// or it is lost with the page.
const PROMO_CODE_FAILURE_COOKIE = 'promoCodeFailure';

// Two minutes: only ever needs to survive one provider round-trip.
const PROMO_CODE_FAILURE_COOKIE_MAX_AGE = 120;

export const setPromoCodeFailureNote = (message: string): void => {
  if (typeof document === 'undefined' || !message) { return; }

  document.cookie = `${PROMO_CODE_FAILURE_COOKIE}=${encodeURIComponent(message)}`
    + `; path=/; max-age=${PROMO_CODE_FAILURE_COOKIE_MAX_AGE}; SameSite=Lax`;
};

/** Read-and-clear: the reason is announced exactly once. */
export const drainPromoCodeFailureNote = (): string | undefined => {
  if (typeof document === 'undefined') { return undefined; }

  const row = document.cookie
    .split('; ')
    .find((r) => r.startsWith(`${PROMO_CODE_FAILURE_COOKIE}=`));

  document.cookie = `${PROMO_CODE_FAILURE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;

  if (!row) { return undefined; }

  try {
    return decodeURIComponent(row.slice(`${PROMO_CODE_FAILURE_COOKIE}=`.length)) || undefined;
  } catch {
    return undefined;
  }
};
